import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const MerchandiseSection = ({ merchandise, onAddToCart, cartItems }) => {
  if (!merchandise || merchandise?.length === 0) return null;

  const getCartQuantity = (itemId, selectedSize = null) => {
    const cartItem = cartItems?.find(item => 
      item?.id === itemId && 
      item?.type === 'merchandise' && 
      (selectedSize ? item?.selectedSize === selectedSize : true)
    );
    return cartItem ? cartItem?.quantity : 0;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(price);
  };

  const MerchandiseItem = ({ item }) => {
    const [selectedSize, setSelectedSize] = React.useState(
      item?.sizes && item?.sizes?.length > 0 ? item?.sizes?.[0]?.size : null
    );
    
    const currentSizeInfo = item?.sizes?.find(s => s?.size === selectedSize);
    const isAvailable = currentSizeInfo ? currentSizeInfo?.stock > 0 : item?.stock > 0;
    const cartQuantity = getCartQuantity(item?.id, selectedSize);
    
    const sizeOptions = item?.sizes?.map(size => ({
      value: size?.size,
      label: `${size?.size} ${size?.stock <= 5 && size?.stock > 0 ? `(${size?.stock} left)` : ''}`,
      disabled: size?.stock === 0
    })) || [];

    const handleAddToCart = (quantity) => {
      const itemToAdd = {
        ...item,
        selectedSize: selectedSize,
        type: 'merchandise'
      };
      onAddToCart(itemToAdd, quantity);
    };

    return (
      <div className="border rounded-lg p-4 bg-background">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Product Image */}
          <div className="w-full sm:w-32 h-32 flex-shrink-0">
            <Image
              src={item?.image}
              alt={item?.name}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          
          {/* Product Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h3 className={`font-medium ${!isAvailable ? 'text-muted-foreground' : 'text-foreground'}`}>
                {item?.name}
              </h3>
              <div className="text-right">
                <span className={`text-lg font-bold ${!isAvailable ? 'text-muted-foreground' : 'text-foreground'}`}>
                  {formatPrice(item?.price)}
                </span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              {item?.description}
            </p>
            
            {/* Size Selection */}
            {item?.sizes && item?.sizes?.length > 0 && (
              <div className="mb-3">
                <Select
                  label="Size"
                  options={sizeOptions}
                  value={selectedSize}
                  onChange={setSelectedSize}
                  className="w-32"
                />
              </div>
            )}
            
            {/* Stock Status */}
            <div className="flex items-center justify-between">
              <div className="text-sm">
                {!isAvailable ? (
                  <span className="text-error">Out of Stock</span>
                ) : (
                  <span className="text-success">
                    {currentSizeInfo ? 
                      `${currentSizeInfo?.stock} in stock` : 
                      `${item?.stock} in stock`
                    }
                  </span>
                )}
              </div>
              
              {/* Add to Cart Controls */}
              <div className="flex items-center gap-2">
                {cartQuantity > 0 && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-lg">
                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="Minus"
                      onClick={() => handleAddToCart(-1)}
                      className="w-6 h-6 p-0"
                    />
                    <span className="font-medium text-primary min-w-[16px] text-center text-sm">
                      {cartQuantity}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="Plus"
                      onClick={() => handleAddToCart(1)}
                      className="w-6 h-6 p-0"
                      disabled={cartQuantity >= 10}
                    />
                  </div>
                )}
                
                {cartQuantity === 0 && (
                  <Button
                    variant={!isAvailable ? "ghost" : "outline"}
                    size="sm"
                    onClick={() => handleAddToCart(1)}
                    disabled={!isAvailable}
                    iconName="ShoppingCart"
                  >
                    Add
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg p-6 mb-8">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="ShoppingBag" size={24} className="text-primary" />
        <h2 className="text-2xl font-semibold text-foreground">Event Merchandise</h2>
      </div>
      <div className="space-y-4">
        {merchandise?.map((item) => (
          <MerchandiseItem key={item?.id} item={item} />
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Icon name="Truck" size={16} className="mt-0.5 flex-shrink-0" />
          <p>
            Merchandise will be available for pickup at the event or can be shipped to your address. 
            Shipping costs will be calculated at checkout.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MerchandiseSection;